// src/components/resources/tools/generators/AdMockup/types.ts
//
// Shared type definitions + ad-spec constants for the Ad Mockup / Ad Preview
// Generator. Extracted verbatim from the former single-file component so the
// placement subcomponents, the pure helpers, and the orchestrator can share one
// source of truth.

export type AdPlatform = 'facebook' | 'instagram' | 'story' | 'tiktok' | 'linkedin' | 'youtube' | 'google-search'
export type Theme = 'light' | 'dark'

/** Placement toggle metadata — label + the export-filename stem. */
export const PLACEMENTS: ReadonlyArray<{ id: AdPlatform; label: string }> = [
  { id: 'facebook', label: 'Facebook Feed' },
  { id: 'instagram', label: 'Instagram Feed' },
  { id: 'story', label: 'Stories / Reels' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'google-search', label: 'Google Search' },
]

/**
 * Per-placement control capabilities. Each placement renders different ad
 * chrome, so it exposes a different subset of fields — driving the controls
 * from a config (rather than ad-hoc `isX` booleans) keeps the form readable as
 * placements grow and makes "which field shows where" auditable in one place.
 * `google-search` is handled by its own controls branch and is intentionally
 * absent here.
 */
export interface PlacementCaps {
  /** Primary text / caption field (+ its label). */
  caption: boolean
  captionLabel: string
  /** Link-card headline. */
  headline: boolean
  /** Link-card description (Facebook-style). */
  description: boolean
  /** Display URL / advertiser domain. */
  displayUrl: boolean
  /** Editable like / comment counts. */
  engagement: boolean
  /** Editable share count (LinkedIn uses reposts, not shares, so it opts out). */
  shares: boolean
  /** Light/Dark theme toggle. Only placements whose preview actually reads
   * `state.theme` (feed cards) expose it — the full-bleed media placements
   * (Stories/Reels, TikTok, YouTube) render on their own dark chrome regardless,
   * so showing a no-op Theme select there would mislead. */
  theme: boolean
  /** Creative upload label (varies by placement). */
  creativeLabel: string
  /** Renders a vertical 9:16 preview (narrower column). */
  vertical: boolean
}
export const CAPS: Record<Exclude<AdPlatform, 'google-search'>, PlacementCaps> = {
  facebook: { caption: true, captionLabel: 'Primary text (caption)', headline: true, description: true, displayUrl: true, engagement: true, shares: true, theme: true, creativeLabel: 'Ad creative image', vertical: false },
  instagram: { caption: true, captionLabel: 'Primary text (caption)', headline: true, description: true, displayUrl: true, engagement: true, shares: true, theme: true, creativeLabel: 'Ad creative image', vertical: false },
  story: { caption: true, captionLabel: 'Caption / overlay text', headline: false, description: false, displayUrl: false, engagement: false, shares: false, theme: false, creativeLabel: 'Background creative', vertical: true },
  tiktok: { caption: true, captionLabel: 'Caption', headline: false, description: false, displayUrl: false, engagement: true, shares: true, theme: false, creativeLabel: 'Video / background creative', vertical: true },
  linkedin: { caption: true, captionLabel: 'Intro text', headline: true, description: false, displayUrl: true, engagement: true, shares: false, theme: true, creativeLabel: 'Ad creative image', vertical: false },
  youtube: { caption: false, captionLabel: '', headline: true, description: false, displayUrl: true, engagement: false, shares: false, theme: false, creativeLabel: 'Video frame', vertical: false },
}

/** 10 MB upload ceiling — an arbitrarily large file becomes an unbounded data
 * URL in React state (tab hang / OOM). Shared across the image-upload
 * generators via `../shared/uploadLimits`. */
export { MAX_UPLOAD_BYTES } from '../shared/uploadLimits'

/**
 * Real platform ad-copy limits, enforced live in the UI (the accuracy moat).
 * Google RSA: headline ≤30, description ≤90, display path ≤15 chars each.
 * Meta primary text isn't hard-capped but truncates with "… See more" at
 * ~125 chars on feed — we warn at that point rather than block.
 * Sources: Google Ads RSA specs; Meta Ads Guide primary-text truncation.
 */
export const SPEC = {
  googleHeadline: 30,
  googleDescription: 90,
  googlePath: 15,
  metaPrimaryTruncate: 125,
} as const

/** Meta's real call-to-action button labels. */
export const CTA_OPTIONS = [
  'Shop Now',
  'Learn More',
  'Sign Up',
  'Download',
  'Book Now',
  'Get Offer',
  'Contact Us',
  'Subscribe',
  'Get Quote',
  'Apply Now',
  'Watch More',
  'Send Message',
] as const
export type Cta = (typeof CTA_OPTIONS)[number]

export interface AdState {
  platform: AdPlatform
  pageName: string
  primaryText: string
  headline: string
  description: string
  displayUrl: string
  cta: Cta
  verified: boolean
  likes: number
  comments: number
  shares: number
  theme: Theme
  // Google Responsive Search Ad fields (text ad — distinct from the Meta feed
  // chrome). Up to 3 headlines + 2 descriptions render in the preview.
  gHeadlines: [string, string, string]
  gDescriptions: [string, string]
  gPath1: string
  gPath2: string
}

// Neutral placeholder defaults (demo-trust): no verified badge, a generic brand
// and URL, and template copy — so an unedited export can't be mistaken for a
// live, endorsed ad for a real company.
export const DEFAULT_STATE: Omit<AdState, 'platform'> = {
  pageName: 'Your Brand',
  primaryText: 'Write the primary text that hooks your audience and makes them stop scrolling.',
  headline: 'Your headline goes here',
  description: 'A short supporting line for your offer.',
  displayUrl: 'yourbrand.com',
  cta: 'Shop Now',
  verified: false,
  likes: 1248,
  comments: 86,
  shares: 42,
  theme: 'light',
  gHeadlines: ['Your Headline Here', 'Free Shipping Over $50', 'Shop the New Collection'],
  gDescriptions: [
    'Write a benefit-led description that earns the click. Mention your offer and a clear CTA.',
    'Trusted by thousands. Fast delivery, easy returns, and prices you will love.',
  ],
  gPath1: 'collection',
  gPath2: 'new',
}

export interface AdMockupProps {
  initialPlatform?: AdPlatform
  trackingSlug?: string
}
